/**
 * Additional Data Intelligence Modals
 * Event, Document, Location, Property modals
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Slider } from '../../components/ui/slider';
import { Separator } from '../../components/ui/separator';
import {
  Zap,
  FileText,
  MapPin,
  Building,
  Upload,
  X,
  Loader2,
  AlertTriangle,
  Gavel,
  FileSearch,
  ShieldAlert,
  Check,
  FileType,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { geocodeAPI, intelligenceAPI, fieldOptionsAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import { DynamicSelect, useFieldOptions } from '../DynamicSelect';
import { OrganisationAutocomplete } from '../OrganisationAutocomplete';

// Icon mapping for event categories
const ICON_MAP = {
  AlertTriangle,
  Gavel,
  FileSearch,
  ShieldAlert,
  Building,
};

// Event Types and Conditional Fields
const EVENT_TYPES = {
  insolvency: {
    label: 'Insolvency',
    icon: AlertTriangle,
    color: 'text-red-400',
    subtypes: [
      'Company Voluntary Arrangement',
      'Administration',
      'Receivership',
      'Liquidation (Voluntary)',
      'Liquidation (Compulsory)',
      'Dissolution',
      'Struck Off',
      'Restored',
    ],
  },
  procurement: {
    label: 'Procurement',
    icon: FileSearch,
    color: 'text-blue-400',
    subtypes: [
      'Contract Award',
      'Contract Notice',
      'Prior Information Notice',
      'Framework Agreement',
      'Dynamic Purchasing System',
      'Subcontract Opportunity',
      'Contract Modification',
      'Contract Cancellation',
    ],
  },
  regulatory: {
    label: 'Regulatory',
    icon: ShieldAlert,
    color: 'text-amber-400',
    subtypes: [
      'Investigation Opened',
      'Investigation Closed',
      'Fine Issued',
      'Warning Issued',
      'License Granted',
      'License Revoked',
      'Enforcement Action',
      'Compliance Order',
      'Sanction Applied',
    ],
  },
  legal: {
    label: 'Legal',
    icon: Gavel,
    color: 'text-purple-400',
    subtypes: [
      'Court Filing',
      'Judgment',
      'Settlement',
      'Appeal',
      'Tribunal Hearing',
      'Class Action',
      'Injunction',
    ],
  },
  corporate: {
    label: 'Corporate',
    icon: Building,
    color: 'text-green-400',
    subtypes: [
      'Merger',
      'Acquisition',
      'Demerger',
      'IPO',
      'Delisting',
      'Board Change',
      'Restructuring',
      'Name Change',
      'Registered Office Change',
    ],
  },
};

const SOURCE_TYPES = ['Manual', 'Companies House', 'Gazette', 'Court Records', 'TED', 'Contracts Finder', 'FCA', 'CMA', 'ICO', 'Other Regulator', 'News', 'Automated'];

const DOCUMENT_TYPES = [
  { value: 'annual_accounts', label: 'Annual Accounts' },
  { value: 'confirmation_statement', label: 'Confirmation Statement' },
  { value: 'articles_of_association', label: 'Articles of Association' },
  { value: 'certificate_of_incorporation', label: 'Certificate of Incorporation' },
  { value: 'court_document', label: 'Court Document' },
  { value: 'gazette_notice', label: 'Gazette Notice' },
  { value: 'contract', label: 'Contract' },
  { value: 'tender_document', label: 'Tender Document' },
  { value: 'regulatory_filing', label: 'Regulatory Filing' },
  { value: 'press_release', label: 'Press Release' },
  { value: 'news_article', label: 'News Article' },
  { value: 'research_report', label: 'Research Report' },
  { value: 'other', label: 'Other' },
];

const PROPERTY_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'retail', label: 'Retail' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'residential', label: 'Residential' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'land', label: 'Land' },
  { value: 'other', label: 'Other' },
];

const TENURE_TYPES = [
  { value: 'freehold', label: 'Freehold' },
  { value: 'leasehold', label: 'Leasehold' },
  { value: 'license', label: 'License' },
  { value: 'unknown', label: 'Unknown' },
];

// Modal Tab Navigation Component
function ModalTabs({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex border-b border-border-default mb-4 -mx-6 px-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-[1px] transition-colors',
            activeTab === tab.id
              ? 'border-accent-primary text-accent-primary'
              : 'border-transparent text-text-tertiary hover:text-text-secondary'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Tag Input Component
function TagInput({ value = [], onChange, placeholder = 'Add tag...' }) {
  const [inputValue, setInputValue] = useState('');
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue('');
    }
  };
  
  const removeTag = (tagToRemove) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };
  
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2 bg-bg-tertiary border border-border-default rounded-lg">
        {value.map((tag, i) => (
          <Badge key={i} variant="secondary" className="bg-accent-primary/20 text-accent-primary gap-1">
            {tag}
            <X className="w-3 h-3 cursor-pointer hover:text-white" onClick={() => removeTag(tag)} />
          </Badge>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[100px] bg-transparent outline-none text-sm text-text-primary placeholder:text-text-tertiary"
        />
      </div>
      <p className="text-xs text-text-tertiary">Press Enter to add</p>
    </div>
  );
}

// ─────────────────────────────────────────────
// EVENT MODAL
// ─────────────────────────────────────────────
export function EventModal({ open, onOpenChange, record, onSave, allRecords = [] }) {
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({
    title: '',
    content: {
      event_category: '',
      event_type: '',
      description: '',
      event_date: '',
      effective_date: '',
      end_date: '',
      is_ongoing: false,
      // Related entities
      related_organisation_id: '',
      related_organisation_name: '',
      related_person_id: '',
      related_person_name: '',
      // Insolvency-specific
      insolvency: {
        practitioner_name: '',
        practitioner_firm: '',
        case_number: '',
        court_name: '',
      },
      // Procurement-specific
      procurement: {
        contract_value: '',
        currency: 'GBP',
        contract_reference: '',
        buyer_name: '',
        cpv_codes: [],
        award_criteria: '',
        lot_number: '',
      },
      // Regulatory-specific
      regulatory: {
        regulator_name: '',
        reference_number: '',
        fine_amount: '',
        penalty_details: '',
        breach_type: '',
      },
      // Source
      source_url: '',
      gazette_reference: '',
    },
    tags: [],
    source: 'Manual',
    confidence_score: 0.8,
  });

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        content: {
          ...form.content,
          ...record.content,
          insolvency: { ...form.content.insolvency, ...record.content?.insolvency },
          procurement: { ...form.content.procurement, ...record.content?.procurement },
          regulatory: { ...form.content.regulatory, ...record.content?.regulatory },
        },
        tags: record.tags || [],
        source: record.source || 'Manual',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      // Reset form
      setForm({
        title: '',
        content: {
          event_category: '',
          event_type: '',
          description: '',
          event_date: '',
          effective_date: '',
          end_date: '',
          is_ongoing: false,
          related_organisation_id: '',
          related_organisation_name: '',
          related_person_id: '',
          related_person_name: '',
          insolvency: { practitioner_name: '', practitioner_firm: '', case_number: '', court_name: '' },
          procurement: { contract_value: '', currency: 'GBP', contract_reference: '', buyer_name: '', cpv_codes: [], award_criteria: '', lot_number: '' },
          regulatory: { regulator_name: '', reference_number: '', fine_amount: '', penalty_details: '', breach_type: '' },
          source_url: '',
          gazette_reference: '',
        },
        tags: [],
        source: 'Manual',
        confidence_score: 0.8,
      });
    }
    setActiveTab('details');
  }, [record, open]);

  const handleSave = () => {
    const data = {
      title: form.title || `${form.content.event_type || 'Event'} - ${form.content.related_organisation_name || 'Unknown'}`,
      record_type: 'event',
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        confidence_score: form.confidence_score,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const organisations = allRecords.filter(r => r.record_type === 'organisation');
  const persons = allRecords.filter(r => r.record_type === 'person');
  const eventCategory = form.content.event_category;
  const eventConfig = EVENT_TYPES[eventCategory];

  const tabs = [
    { id: 'details', label: 'Event Details' },
    { id: 'entities', label: 'Related Entities' },
    ...(eventCategory ? [{ id: 'specific', label: eventConfig?.label || 'Details' }] : []),
    { id: 'source', label: 'Source' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-400" />
            {record ? 'Edit Event' : 'Record Event'}
          </DialogTitle>
        </DialogHeader>
        
        <ModalTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Tab 1: Event Details */}
            {activeTab === 'details' && (
              <>
                <div>
                  <Label>Event Category <span className="text-red-400">*</span></Label>
                  <DynamicSelect
                    fieldType="event_category"
                    value={form.content.event_category}
                    onValueChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, event_category: v, event_type: '' }
                    }))}
                    placeholder="Select category"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>

                {eventCategory && (
                  <div>
                    <Label>Event Type <span className="text-red-400">*</span></Label>
                    <DynamicSelect
                      fieldType="event_subtype"
                      parentValue={eventCategory}
                      value={form.content.event_type}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, event_type: v }
                      }))}
                      placeholder="Select type"
                      triggerClassName="bg-bg-tertiary border-border-default"
                    />
                  </div>
                )}

                <div>
                  <Label>Event Title</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Brief title for this event"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.content.description}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, description: e.target.value }
                    }))}
                    placeholder="Detailed description of the event..."
                    className="bg-bg-tertiary border-border-default min-h-[100px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Event Date <span className="text-red-400">*</span></Label>
                    <Input
                      type="date"
                      value={form.content.event_date}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, event_date: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Effective Date</Label>
                    <Input
                      type="date"
                      value={form.content.effective_date}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, effective_date: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div>
                    <Label className="text-base">Ongoing Event</Label>
                    <p className="text-sm text-text-tertiary">Event is still in progress</p>
                  </div>
                  <Switch
                    checked={form.content.is_ongoing}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, is_ongoing: v }
                    }))}
                  />
                </div>

                {!form.content.is_ongoing && (
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={form.content.end_date}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, end_date: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                )}
              </>
            )}

            {/* Tab 2: Related Entities */}
            {activeTab === 'entities' && (
              <>
                <div>
                  <Label>Related Organisation</Label>
                  <OrganisationAutocomplete
                    value={form.content.related_organisation_name}
                    onChange={(name) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, related_organisation_name: name }
                    }))}
                    onSelect={(org) => {
                      if (org) {
                        setForm(prev => ({
                          ...prev,
                          content: {
                            ...prev.content,
                            related_organisation_id: org.id,
                            related_organisation_name: org.name
                          }
                        }));
                      }
                    }}
                    placeholder="Search or type organisation name..."
                  />
                </div>

                <Separator />

                <div>
                  <Label>Related Person</Label>
                  <Select
                    value={form.content.related_person_id || undefined}
                    onValueChange={(v) => {
                      const person = persons.find(p => p.id === v);
                      setForm(prev => ({
                        ...prev,
                        content: {
                          ...prev.content,
                          related_person_id: v,
                          related_person_name: person?.title || ''
                        }
                      }));
                    }}
                  >
                    <SelectTrigger className="bg-bg-tertiary border-border-default">
                      <SelectValue placeholder="Select person" />
                    </SelectTrigger>
                    <SelectContent>
                      {persons.map((person) => (
                        <SelectItem key={person.id} value={person.id}>{person.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Person Name (Manual)</Label>
                  <Input
                    value={form.content.related_person_name}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, related_person_name: e.target.value }
                    }))}
                    placeholder="Person name if not in database"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Tags</Label>
                  <TagInput value={form.tags} onChange={(v) => setForm(prev => ({ ...prev, tags: v }))} />
                </div>
              </>
            )}

            {/* Tab 3: Category-Specific Fields */}
            {activeTab === 'specific' && eventCategory === 'insolvency' && (
              <>
                <div className="bg-red-900/10 border border-red-800 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">Insolvency Event Details</span>
                  </div>
                  <p className="text-sm text-text-secondary">Enter details specific to this insolvency event.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Practitioner Name</Label>
                    <Input
                      value={form.content.insolvency.practitioner_name}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, insolvency: { ...prev.content.insolvency, practitioner_name: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Practitioner Firm</Label>
                    <Input
                      value={form.content.insolvency.practitioner_firm}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, insolvency: { ...prev.content.insolvency, practitioner_firm: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Case Number</Label>
                    <Input
                      value={form.content.insolvency.case_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, insolvency: { ...prev.content.insolvency, case_number: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                  <div>
                    <Label>Court Name</Label>
                    <Input
                      value={form.content.insolvency.court_name}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, insolvency: { ...prev.content.insolvency, court_name: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'specific' && eventCategory === 'procurement' && (
              <>
                <div className="bg-blue-900/10 border border-blue-800 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-2">
                    <FileSearch className="w-4 h-4" />
                    <span className="font-medium">Procurement Event Details</span>
                  </div>
                  <p className="text-sm text-text-secondary">Enter contract and procurement-specific information.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contract Value</Label>
                    <Input
                      type="number"
                      value={form.content.procurement.contract_value}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, procurement: { ...prev.content.procurement, contract_value: e.target.value } }
                      }))}
                      placeholder="0.00"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select
                      value={form.content.procurement.currency}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, procurement: { ...prev.content.procurement, currency: v } }
                      }))}
                    >
                      <SelectTrigger className="bg-bg-tertiary border-border-default">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contract Reference</Label>
                    <Input
                      value={form.content.procurement.contract_reference}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, procurement: { ...prev.content.procurement, contract_reference: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                  <div>
                    <Label>Lot Number</Label>
                    <Input
                      value={form.content.procurement.lot_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, procurement: { ...prev.content.procurement, lot_number: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div>
                  <Label>Buyer Name</Label>
                  <Input
                    value={form.content.procurement.buyer_name}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, procurement: { ...prev.content.procurement, buyer_name: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Award Criteria</Label>
                  <Textarea
                    value={form.content.procurement.award_criteria}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, procurement: { ...prev.content.procurement, award_criteria: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
              </>
            )}

            {activeTab === 'specific' && eventCategory === 'regulatory' && (
              <>
                <div className="bg-amber-900/10 border border-amber-800 p-4 rounded-lg mb-4">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <ShieldAlert className="w-4 h-4" />
                    <span className="font-medium">Regulatory Event Details</span>
                  </div>
                  <p className="text-sm text-text-secondary">Enter regulatory action and enforcement details.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Regulator Name</Label>
                    <Input
                      value={form.content.regulatory.regulator_name}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, regulatory: { ...prev.content.regulatory, regulator_name: e.target.value } }
                      }))}
                      placeholder="e.g., FCA, CMA, ICO"
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Reference Number</Label>
                    <Input
                      value={form.content.regulatory.reference_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, regulatory: { ...prev.content.regulatory, reference_number: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fine Amount (£)</Label>
                    <Input
                      type="number"
                      value={form.content.regulatory.fine_amount}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, regulatory: { ...prev.content.regulatory, fine_amount: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Breach Type</Label>
                    <Input
                      value={form.content.regulatory.breach_type}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, regulatory: { ...prev.content.regulatory, breach_type: e.target.value } }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div>
                  <Label>Penalty Details</Label>
                  <Textarea
                    value={form.content.regulatory.penalty_details}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, regulatory: { ...prev.content.regulatory, penalty_details: e.target.value } }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
              </>
            )}

            {/* Tab 4: Source */}
            {activeTab === 'source' && (
              <>
                <div>
                  <Label>Source Type</Label>
                  <DynamicSelect
                    fieldType="source_type"
                    value={form.source}
                    onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                    placeholder="Select source"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Source URL</Label>
                  <Input
                    value={form.content.source_url}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, source_url: e.target.value }
                    }))}
                    placeholder="https://..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Confidence: {Math.round(form.confidence_score * 100)}%</Label>
                  <Slider
                    value={[form.confidence_score]}
                    onValueChange={([v]) => setForm(prev => ({ ...prev, confidence_score: v }))}
                    min={0}
                    max={1}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!form.content.event_category || !form.content.event_type || !form.content.event_date}
          >
            Save Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// DOCUMENT MODAL
// ─────────────────────────────────────────────
export function DocumentModal({ open, onOpenChange, record, onSave, allRecords = [] }) {
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({
    title: '',
    content: {
      document_type: '',
      description: '',
      file_name: '',
      file_path: '',
      file_size: 0,
      file_type: '',
      original_url: '',
      publication_date: '',
      // Related entities
      related_organisation_id: '',
      related_organisation_name: '',
      related_event_id: '',
      // Extraction
      extract_enabled: false,
      extraction_status: 'pending',
      extracted_text: '',
      extracted_entities: [],
      extracted_dates: [],
      extracted_amounts: [],
      // Metadata
      author: '',
      publisher: '',
      reference_number: '',
      version: '',
    },
    tags: [],
    source: 'Manual',
    confidence_score: 0.8,
  });
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        content: { ...form.content, ...record.content },
        tags: record.tags || [],
        source: record.source || 'Manual',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      setForm({
        title: '',
        content: {
          document_type: '',
          description: '',
          file_name: '',
          file_path: '',
          file_size: 0,
          file_type: '',
          original_url: '',
          publication_date: '',
          related_organisation_id: '',
          related_organisation_name: '',
          related_event_id: '',
          extract_enabled: false,
          extraction_status: 'pending',
          extracted_text: '',
          extracted_entities: [],
          extracted_dates: [],
          extracted_amounts: [],
          author: '',
          publisher: '',
          reference_number: '',
          version: '',
        },
        tags: [],
        source: 'Manual',
        confidence_score: 0.8,
      });
    }
    setActiveTab('details');
  }, [record, open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({
        ...prev,
        title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
        content: {
          ...prev.content,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type || 'application/octet-stream',
          file_path: `/uploads/documents/${Date.now()}_${file.name}`,
        }
      }));
    }
  };

  const handleExtract = async () => {
    setExtracting(true);
    // Mock extraction - in production this would call Claude API
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setForm(prev => ({
      ...prev,
      content: {
        ...prev.content,
        extraction_status: 'completed',
        extracted_text: 'Mock extracted text from document...',
        extracted_entities: ['Company A Ltd', 'John Smith'],
        extracted_dates: ['2024-01-15', '2024-03-01'],
        extracted_amounts: ['£50,000', '£125,000'],
      }
    }));
    setExtracting(false);
    toast.success('Document extracted (mock)');
  };

  const handleSave = () => {
    const data = {
      title: form.title || form.content.file_name || 'Untitled Document',
      record_type: 'document',
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        confidence_score: form.confidence_score,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const organisations = allRecords.filter(r => r.record_type === 'organisation');
  const events = allRecords.filter(r => r.record_type === 'event');

  const tabs = [
    { id: 'details', label: 'Document Details' },
    { id: 'upload', label: 'Upload / Link' },
    { id: 'extract', label: 'AI Extraction' },
    { id: 'metadata', label: 'Metadata' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-400" />
            {record ? 'Edit Document' : 'Add Document'}
          </DialogTitle>
        </DialogHeader>
        
        <ModalTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Tab 1: Document Details */}
            {activeTab === 'details' && (
              <>
                <div>
                  <Label>Document Title <span className="text-red-400">*</span></Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Document title"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Document Type <span className="text-red-400">*</span></Label>
                  <DynamicSelect
                    fieldType="document_type"
                    value={form.content.document_type}
                    onValueChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, document_type: v }
                    }))}
                    placeholder="Select type"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.content.description}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, description: e.target.value }
                    }))}
                    placeholder="Brief description of the document..."
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Related Organisation</Label>
                  <OrganisationAutocomplete
                    value={form.content.related_organisation_name}
                    onChange={(name) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, related_organisation_name: name }
                    }))}
                    onSelect={(org) => {
                      if (org) {
                        setForm(prev => ({
                          ...prev,
                          content: {
                            ...prev.content,
                            related_organisation_id: org.id,
                            related_organisation_name: org.name
                          }
                        }));
                      }
                    }}
                    placeholder="Search or type organisation name..."
                  />
                </div>

                <div>
                  <Label>Publication Date</Label>
                  <Input
                    type="date"
                    value={form.content.publication_date}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, publication_date: e.target.value }
                    }))}
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Tags</Label>
                  <TagInput value={form.tags} onChange={(v) => setForm(prev => ({ ...prev, tags: v }))} />
                </div>
              </>
            )}

            {/* Tab 2: Upload / Link */}
            {activeTab === 'upload' && (
              <>
                <div className="border-2 border-dashed border-border-default rounded-lg p-8 text-center">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-text-tertiary" />
                  <p className="text-text-secondary mb-2">Drag and drop a file or click to browse</p>
                  <p className="text-xs text-text-tertiary mb-4">Supports PDF, Word, Excel, images, and more</p>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id="document-upload"
                  />
                  <label htmlFor="document-upload">
                    <Button asChild variant="outline">
                      <span>Choose File</span>
                    </Button>
                  </label>
                </div>

                {form.content.file_name && (
                  <div className="flex items-center gap-3 p-4 bg-bg-tertiary rounded-lg">
                    <FileType className="w-8 h-8 text-accent-primary" />
                    <div className="flex-1">
                      <p className="text-text-primary font-medium">{form.content.file_name}</p>
                      <p className="text-xs text-text-tertiary">
                        {form.content.file_type} • {(form.content.file_size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, file_name: '', file_path: '', file_size: 0, file_type: '' }
                      }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                <Separator />

                <div>
                  <Label>Or enter document URL</Label>
                  <Input
                    value={form.content.original_url}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, original_url: e.target.value }
                    }))}
                    placeholder="https://..."
                    className="bg-bg-tertiary border-border-default"
                  />
                  <p className="text-xs text-text-tertiary mt-1">Link to the original document source</p>
                </div>
              </>
            )}

            {/* Tab 3: AI Extraction */}
            {activeTab === 'extract' && (
              <>
                <div className="bg-teal-900/10 border border-teal-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-teal-400">
                      <FileSearch className="w-4 h-4" />
                      <span className="font-medium">AI Document Extraction</span>
                    </div>
                    <Badge className={cn(
                      'border',
                      form.content.extraction_status === 'completed' ? 'bg-green-900/20 text-green-400 border-green-800' :
                      form.content.extraction_status === 'failed' ? 'bg-red-900/20 text-red-400 border-red-800' :
                      'bg-amber-900/20 text-amber-400 border-amber-800'
                    )}>
                      {form.content.extraction_status}
                    </Badge>
                  </div>
                  <p className="text-sm text-text-secondary">
                    Use Claude AI to extract key entities, dates, and amounts from this document.
                  </p>
                </div>

                <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-lg">
                  <div>
                    <Label className="text-base">Scrape this document</Label>
                    <p className="text-sm text-text-tertiary">Enable AI extraction when document is saved</p>
                  </div>
                  <Switch
                    checked={form.content.extract_enabled}
                    onCheckedChange={(v) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, extract_enabled: v }
                    }))}
                  />
                </div>

                {form.content.extract_enabled && (
                  <Button
                    onClick={handleExtract}
                    disabled={extracting}
                    className="w-full"
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <FileSearch className="w-4 h-4 mr-2" />
                        Run Extraction Now
                      </>
                    )}
                  </Button>
                )}

                {form.content.extraction_status === 'completed' && (
                  <>
                    <Separator />
                    
                    <div>
                      <Label>Extracted Entities</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.content.extracted_entities.map((entity, i) => (
                          <Badge key={i} variant="outline">{entity}</Badge>
                        ))}
                        {form.content.extracted_entities.length === 0 && (
                          <span className="text-text-tertiary text-sm">No entities found</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label>Extracted Dates</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.content.extracted_dates.map((date, i) => (
                          <Badge key={i} variant="outline" className="bg-blue-900/20 text-blue-400 border-blue-800">{date}</Badge>
                        ))}
                        {form.content.extracted_dates.length === 0 && (
                          <span className="text-text-tertiary text-sm">No dates found</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label>Extracted Amounts</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.content.extracted_amounts.map((amount, i) => (
                          <Badge key={i} variant="outline" className="bg-green-900/20 text-green-400 border-green-800">{amount}</Badge>
                        ))}
                        {form.content.extracted_amounts.length === 0 && (
                          <span className="text-text-tertiary text-sm">No amounts found</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Tab 4: Metadata */}
            {activeTab === 'metadata' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Author</Label>
                    <Input
                      value={form.content.author}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, author: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Publisher</Label>
                    <Input
                      value={form.content.publisher}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, publisher: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Reference Number</Label>
                    <Input
                      value={form.content.reference_number}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, reference_number: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default font-mono"
                    />
                  </div>
                  <div>
                    <Label>Version</Label>
                    <Input
                      value={form.content.version}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        content: { ...prev.content, version: e.target.value }
                      }))}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>

                <div>
                  <Label>Source Type</Label>
                  <DynamicSelect
                    fieldType="source_type"
                    value={form.source}
                    onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                    placeholder="Select source"
                    triggerClassName="bg-bg-tertiary border-border-default"
                  />
                </div>

                <div>
                  <Label>Confidence: {Math.round(form.confidence_score * 100)}%</Label>
                  <Slider
                    value={[form.confidence_score]}
                    onValueChange={([v]) => setForm(prev => ({ ...prev, confidence_score: v }))}
                    min={0}
                    max={1}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </>
            )}
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!form.title || !form.content.document_type}
          >
            Save Document
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// LOCATION MODAL
// ─────────────────────────────────────────────
export function LocationModal({ open, onOpenChange, record, onSave }) {
  const [form, setForm] = useState({
    title: '',
    content: {
      address_line1: '',
      address_line2: '',
      town_city: '',
      county: '',
      postcode: '',
      country: 'England',
      // Geocoded data
      postcode_area: '',
      local_authority: '',
      region: '',
      parliamentary_constituency: '',
      lsoa_code: '',
      msoa_code: '',
      lat: null,
      lng: null,
      // Additional
      location_type: '',
      description: '',
    },
    tags: [],
    source: 'Manual',
    confidence_score: 0.8,
  });
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        content: { ...form.content, ...record.content },
        tags: record.tags || [],
        source: record.source || 'Manual',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      setForm({
        title: '',
        content: {
          address_line1: '',
          address_line2: '',
          town_city: '',
          county: '',
          postcode: '',
          country: 'England',
          postcode_area: '',
          local_authority: '',
          region: '',
          parliamentary_constituency: '',
          lsoa_code: '',
          msoa_code: '',
          lat: null,
          lng: null,
          location_type: '',
          description: '',
        },
        tags: [],
        source: 'Manual',
        confidence_score: 0.8,
      });
    }
  }, [record, open]);

  const handleGeocode = async () => {
    if (!form.content.postcode) {
      toast.error('Please enter a postcode');
      return;
    }
    
    setGeocoding(true);
    try {
      const response = await geocodeAPI.geocode(form.content.postcode);
      const data = response.data;
      
      setForm(prev => ({
        ...prev,
        content: {
          ...prev.content,
          postcode_area: data.postcode_area || '',
          local_authority: data.local_authority_name || '',
          region: data.region || '',
          country: data.country || 'England',
          parliamentary_constituency: data.parliamentary_constituency || '',
          lsoa_code: data.lsoa_code || '',
          msoa_code: data.msoa_code || '',
          lat: data.lat,
          lng: data.lng,
        }
      }));
      toast.success('Postcode geocoded successfully');
    } catch (error) {
      toast.error('Failed to geocode postcode');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = () => {
    const data = {
      title: form.title || `${form.content.address_line1}, ${form.content.postcode}`,
      record_type: 'location',
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        confidence_score: form.confidence_score,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-400" />
            {record ? 'Edit Location' : 'Add Location'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            <div>
              <Label>Location Name</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Head Office, Warehouse A"
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div>
              <Label>Address Line 1 <span className="text-red-400">*</span></Label>
              <Input
                value={form.content.address_line1}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  content: { ...prev.content, address_line1: e.target.value }
                }))}
                placeholder="Street address"
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div>
              <Label>Address Line 2</Label>
              <Input
                value={form.content.address_line2}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  content: { ...prev.content, address_line2: e.target.value }
                }))}
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Town / City</Label>
                <Input
                  value={form.content.town_city}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, town_city: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>County</Label>
                <Input
                  value={form.content.county}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, county: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Postcode <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    value={form.content.postcode}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, postcode: e.target.value.toUpperCase() }
                    }))}
                    placeholder="e.g., SW1A 1AA"
                    className="bg-bg-tertiary border-border-default font-mono"
                  />
                  <Button
                    variant="outline"
                    onClick={handleGeocode}
                    disabled={geocoding}
                  >
                    {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Country</Label>
                <Select
                  value={form.content.country}
                  onValueChange={(v) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, country: v }
                  }))}
                >
                  <SelectTrigger className="bg-bg-tertiary border-border-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="England">England</SelectItem>
                    <SelectItem value="Wales">Wales</SelectItem>
                    <SelectItem value="Scotland">Scotland</SelectItem>
                    <SelectItem value="Northern Ireland">Northern Ireland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.content.lat && (
              <div className="bg-green-900/10 border border-green-800 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Geocoded Successfully</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-text-secondary">
                  <div><span className="text-text-tertiary">Region:</span> {form.content.region}</div>
                  <div><span className="text-text-tertiary">Local Authority:</span> {form.content.local_authority}</div>
                  <div><span className="text-text-tertiary">Constituency:</span> {form.content.parliamentary_constituency}</div>
                  <div><span className="text-text-tertiary">Coordinates:</span> {form.content.lat?.toFixed(4)}, {form.content.lng?.toFixed(4)}</div>
                </div>
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Textarea
                value={form.content.description}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  content: { ...prev.content, description: e.target.value }
                }))}
                placeholder="Additional details about this location..."
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div>
              <Label>Tags</Label>
              <TagInput value={form.tags} onChange={(v) => setForm(prev => ({ ...prev, tags: v }))} />
            </div>

            <div>
              <Label>Source Type</Label>
              <DynamicSelect
                fieldType="source_type"
                value={form.source}
                onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}
                placeholder="Select source"
                triggerClassName="bg-bg-tertiary border-border-default"
              />
            </div>
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!form.content.address_line1 || !form.content.postcode}
          >
            Save Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// PROPERTY MODAL
// ─────────────────────────────────────────────
export function PropertyModal({ open, onOpenChange, record, onSave, allRecords = [] }) {
  const [form, setForm] = useState({
    title: '',
    content: {
      property_type: '',
      tenure: '',
      // Address
      address_line1: '',
      address_line2: '',
      town_city: '',
      postcode: '',
      country: 'England',
      // Geocoded
      postcode_area: '',
      local_authority: '',
      region: '',
      lat: null,
      lng: null,
      // Property details
      floor_area_sqft: '',
      land_area_acres: '',
      year_built: '',
      epc_rating: '',
      // Ownership
      owner_organisation_id: '',
      owner_organisation_name: '',
      title_number: '',
      // Value
      estimated_value: '',
      last_sale_price: '',
      last_sale_date: '',
      // Additional
      description: '',
    },
    tags: [],
    source: 'Manual',
    confidence_score: 0.8,
  });
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    if (record) {
      setForm({
        title: record.title || '',
        content: { ...form.content, ...record.content },
        tags: record.tags || [],
        source: record.source || 'Manual',
        confidence_score: record.content?.confidence_score || 0.8,
      });
    } else {
      setForm({
        title: '',
        content: {
          property_type: '',
          tenure: '',
          address_line1: '',
          address_line2: '',
          town_city: '',
          postcode: '',
          country: 'England',
          postcode_area: '',
          local_authority: '',
          region: '',
          lat: null,
          lng: null,
          floor_area_sqft: '',
          land_area_acres: '',
          year_built: '',
          epc_rating: '',
          owner_organisation_id: '',
          owner_organisation_name: '',
          title_number: '',
          estimated_value: '',
          last_sale_price: '',
          last_sale_date: '',
          description: '',
        },
        tags: [],
        source: 'Manual',
        confidence_score: 0.8,
      });
    }
  }, [record, open]);

  const handleGeocode = async () => {
    if (!form.content.postcode) {
      toast.error('Please enter a postcode');
      return;
    }
    
    setGeocoding(true);
    try {
      const response = await geocodeAPI.geocode(form.content.postcode);
      const data = response.data;
      
      setForm(prev => ({
        ...prev,
        content: {
          ...prev.content,
          postcode_area: data.postcode_area || '',
          local_authority: data.local_authority_name || '',
          region: data.region || '',
          lat: data.lat,
          lng: data.lng,
        }
      }));
      toast.success('Postcode geocoded');
    } catch (error) {
      toast.error('Failed to geocode');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = () => {
    const data = {
      title: form.title || `${form.content.address_line1}, ${form.content.postcode}`,
      record_type: 'property',
      content: {
        schema_version: 'uk-intel-v1',
        ...form.content,
        confidence_score: form.confidence_score,
      },
      tags: form.tags,
      source: form.source,
    };
    onSave(data);
  };

  const organisations = allRecords.filter(r => r.record_type === 'organisation');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-surface-card border-border-default max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-text-primary flex items-center gap-2">
            <Building className="w-5 h-5 text-cyan-400" />
            {record ? 'Edit Property' : 'Add Property'}
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            <div>
              <Label>Property Name</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Unit 5, Industrial Estate"
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Property Type <span className="text-red-400">*</span></Label>
                <DynamicSelect
                  fieldType="property_type"
                  value={form.content.property_type}
                  onValueChange={(v) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, property_type: v }
                  }))}
                  placeholder="Select type"
                  triggerClassName="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Tenure</Label>
                <DynamicSelect
                  fieldType="tenure_type"
                  value={form.content.tenure}
                  onValueChange={(v) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, tenure: v }
                  }))}
                  placeholder="Select tenure"
                  triggerClassName="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <Separator />

            <div>
              <Label>Address Line 1 <span className="text-red-400">*</span></Label>
              <Input
                value={form.content.address_line1}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  content: { ...prev.content, address_line1: e.target.value }
                }))}
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Town / City</Label>
                <Input
                  value={form.content.town_city}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, town_city: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Postcode <span className="text-red-400">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    value={form.content.postcode}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      content: { ...prev.content, postcode: e.target.value.toUpperCase() }
                    }))}
                    className="bg-bg-tertiary border-border-default font-mono"
                  />
                  <Button variant="outline" onClick={handleGeocode} disabled={geocoding}>
                    {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Floor Area (sqft)</Label>
                <Input
                  type="number"
                  value={form.content.floor_area_sqft}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, floor_area_sqft: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Land Area (acres)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.content.land_area_acres}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, land_area_acres: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Year Built</Label>
                <Input
                  type="number"
                  value={form.content.year_built}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, year_built: e.target.value }
                  }))}
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title Number</Label>
                <Input
                  value={form.content.title_number}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, title_number: e.target.value }
                  }))}
                  placeholder="Land Registry title number"
                  className="bg-bg-tertiary border-border-default font-mono"
                />
              </div>
              <div>
                <Label>EPC Rating</Label>
                <DynamicSelect
                  fieldType="epc_rating"
                  value={form.content.epc_rating}
                  onValueChange={(v) => setForm(prev => ({
                    ...prev,
                    content: { ...prev.content, epc_rating: v }
                  }))}
                  placeholder="Select rating"
                  triggerClassName="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <div>
              <Label>Owner Organisation</Label>
              <Select
                value={form.content.owner_organisation_id || undefined}
                onValueChange={(v) => {
                  const org = organisations.find(o => o.id === v);
                  setForm(prev => ({
                    ...prev,
                    content: {
                      ...prev.content,
                      owner_organisation_id: v,
                      owner_organisation_name: org?.title || ''
                    }
                  }));
                }}
              >
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select organisation" />
                </SelectTrigger>
                <SelectContent>
                  {organisations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tags</Label>
              <TagInput value={form.tags} onChange={(v) => setForm(prev => ({ ...prev, tags: v }))} />
            </div>
          </div>
        </ScrollArea>
        
        <DialogFooter className="border-t border-border-default pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!form.content.property_type || !form.content.address_line1 || !form.content.postcode}
          >
            Save Property
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
